import { createProvider, createClientToolRegistry } from "@microsoft/voice-widget-core-client";
import type {
  AgentMode,
  ConnectionStatus,
  SdpNegotiator,
  SessionGrant,
  SessionRequester,
  SessionRequest,
  VoiceAgentProviderEvents,
} from "@microsoft/voice-widget-core-client";
import {
  makeAuthEndpointNegotiator,
  makeAuthEndpointSessionRequester,
  makeAuthEndpointSessionEnder,
  makeAuthEndpointSessionEventSender,
} from "./authEndpoint.js";
import type { AuthEndpointTelemetryContext } from "./authEndpoint.js";
import {
  createTelemetryEmitter,
  generateId,
  token,
  validateStatus,
  validateSessionId,
} from "./telemetry.js";
import type { AttemptContext } from "./telemetry.js";
import type {
  VoiceAgentCore,
  VoiceAgentError,
  VoiceAgentOptions,
  VoiceAgentState,
} from "./types.js";

/**
 * Headless voice-agent core: owns the provider lifecycle + session state and emits
 * it via subscribe/getState. No DOM, no Preact, no shell — a BYO-UI consumer and our
 * Preact shell subscribe to the same instance. `getOutputLevel` is pull-only (kept
 * out of the snapshot because it changes ~60fps).
 */
export function createVoiceAgent(opts: VoiceAgentOptions): VoiceAgentCore {
  const provider = createProvider(opts.provider);
  if (!provider) {
    throw new Error(
      `createVoiceAgent: no provider registered for "${opts.provider}". ` +
        `Import the provider package (e.g. @microsoft/voice-widget-provider-voicelive) to self-register it.`,
    );
  }
  // Fail fast on a malformed inline config. A widget-id-only (empty) config defers
  // to the broker-resolved config in the grant, so it is validated at connect time.
  if (Object.keys(opts.config).length > 0) {
    provider.parseConfig(opts.config);
  }

  const registry = createClientToolRegistry(opts.clientTools);
  // The static snapshot is "bound" once handed to provider.connect for the current attempt.
  // registerClientTool warns only when a registration lands AFTER the current attempt's
  // snapshot was bound on a provider without live lookup — not merely because status is
  // "connecting", and not after the session has ended.
  let snapshotBoundGen: number | undefined;
  function clearSnapshotFor(gen: number): void {
    if (snapshotBoundGen === gen) snapshotBoundGen = undefined;
  }
  function warnIfBound(action: string, name: string): void {
    const features = provider!.capabilities.features;
    const supportsStatic = features.clientTools === true;
    const supportsDynamic = features.dynamicClientTools === true;
    if (!supportsStatic && !supportsDynamic) {
      // The provider declares no client-tool support at all: a registration is futile on
      // every connect, so "applies to the next connect" would be misleading — say so plainly.
      console.warn(
        `[voice-agent] provider "${provider!.name}" does not support client tools; ` +
          `"${name}" ${action} will have no effect.`,
      );
    } else if (snapshotBoundGen === generation && !supportsDynamic) {
      console.warn(
        `[voice-agent] client tool "${name}" ${action} after connect on a provider without ` +
          "dynamic client tools; it will not affect the current session (applies to the next connect).",
      );
    }
  }

  let state: VoiceAgentState = Object.freeze({
    status: "idle",
    mode: null,
    muted: false,
    error: null,
  });
  const listeners = new Set<() => void>();

  function setState(patch: Partial<VoiceAgentState>): void {
    const next = { ...state, ...patch };
    // Short-circuit no-op updates (e.g. a provider re-emitting the current status, or
    // setMuted(false) while already unmuted): keep the snapshot identity stable so a
    // useSyncExternalStore consumer does not re-render on non-changes. `error` compares
    // by reference — a fresh error object is always treated as a real change.
    if (
      next.status === state.status &&
      next.mode === state.mode &&
      next.muted === state.muted &&
      next.error === state.error
    ) {
      return;
    }
    const mutedChanged = next.muted !== state.muted;
    state = Object.freeze(next);
    for (const l of listeners) l();
    if (mutedChanged) opts.onMuted?.(next.muted);
  }

  // Monotonic attempt counter: each start()/stop()/destroy() bumps it so a superseded
  // attempt's in-flight connect() and late events are ignored or torn down.
  let generation = 0;

  // One telemetry instance ID for this core's lifetime. The emitter owns the
  // per-attempt context (createAttempt/retireAttempt) and fault-isolates onTelemetryEvent.
  const widgetInstanceId = generateId();
  const telemetry = createTelemetryEmitter({
    widgetInstanceId,
    onTelemetryEvent: opts.onTelemetryEvent,
    telemetryConsole: opts.telemetryConsole,
  });

  /** Provider events scoped to one connection attempt (`gen`/`ctx`). */
  function attemptEvents(
    gen: number,
    ctx: AttemptContext,
  ): {
    events: VoiceAgentProviderEvents;
    surfacedError: () => boolean;
  } {
    let surfaced = false;
    const events: VoiceAgentProviderEvents = {
      onStatusChange(s: ConnectionStatus) {
        // Telemetry observes the raw transition BEFORE the generation guard, so a stale
        // (superseded) attempt still emits — statusFor() stamps it rather than dropping it.
        // An invalid status suppresses the whole event because status is its entire payload.
        const vs = validateStatus(s);
        if (vs) telemetry.emit(ctx, "status_changed", { status: vs }, generation);
        // Status-only failure path: onStatusChange("error") with no preceding onError still
        // tags a provider failure. The failureEmitted latch (and `surfaced`) dedupe it.
        if (s === "error" && !surfaced) {
          telemetry.emitFailureOnce(
            ctx,
            "provider",
            { code: "error", recoverable: false },
            generation,
          );
        }
        if (gen !== generation) return;
        // Once this attempt surfaced onError, ignore later non-error status from the
        // same attempt so the error stays pinned until the next start() (preserves the
        // error <-> status biconditional against a provider's trailing "death rattle"
        // disconnect). `recoverable: true` means "a retry via start() may succeed", NOT
        // "the session auto-recovers in place" - the v1 provider never emits onError then
        // reconnects on the same attempt. NOTE the asymmetry: a *synthetic* error (status
        // -> "error" with no onError) does not set `surfaced`, so it is intentionally
        // weaker and a trailing "disconnected" supersedes it.
        if (surfaced && s !== "error") return;
        if (s === "error") {
          clearSnapshotFor(gen);
          setState({
            status: "error",
            mode: null, // terminal: no active turn — never carry a stale mode into error
            // Synthesize minimal detail if status errored without an onError, so the
            // biconditional error!==null <-> status==="error" always holds.
            error: state.error ?? {
              code: "error",
              message: "connection error",
              recoverable: false,
            },
            muted: false,
          });
        } else if (s === "disconnected") {
          clearSnapshotFor(gen);
          // Terminal: clear the turn mode (a stale "speaking" must not outlive the
          // session) and reset mute so the next session starts fresh/unmuted.
          setState({ status: s, mode: null, error: null, muted: false });
        } else {
          setState({ status: s, error: null }); // idle/connecting/connected: keep mode+mute
        }
        opts.onStatusChange?.(s);
      },
      onModeChange(m: AgentMode) {
        if (gen !== generation) return;
        setState({ mode: m });
        opts.onModeChange?.(m);
      },
      onError(e: VoiceAgentError) {
        // Emit before the guard so a retired attempt's failure is still recorded; the
        // per-attempt latch dedupes against the status-only path and a repeated onError.
        telemetry.emitFailureOnce(
          ctx,
          "provider",
          { code: e?.code, recoverable: e?.recoverable },
          generation,
        );
        if (gen !== generation) return;
        surfaced = true;
        clearSnapshotFor(gen);
        // A provider may surface a terminal failure via onError alone. Single atomic
        // update: status + mode reset (no stale turn) + error + mute reset.
        setState({ status: "error", mode: null, error: e, muted: false });
        opts.onError?.(e);
      },
      onTranscript(m) {
        if (gen !== generation) return;
        opts.onTranscript?.(m);
      },
      onConnect(info) {
        // The provider is a third sessionId source. Record it before the guard and emit
        // session_id_resolved only when it differs from the value already on the attempt.
        const sid = info?.sessionId;
        if (sid && sid !== ctx.sessionId) {
          const previousSessionId = ctx.sessionId;
          ctx.sessionId = sid;
          telemetry.emit(
            ctx,
            "session_id_resolved",
            { source: "provider", previousSessionId },
            generation,
          );
        }
        // Emit before the guard so a superseded attempt's connection is still recorded;
        // the per-attempt latch keeps it to one event per successful connection.
        telemetry.emitConnectionOnce(ctx, info?.transport, generation);
        if (gen !== generation) return;
        opts.onConnect?.(info);
      },
      onDisconnect(info) {
        // Emit before the guard so a superseded attempt's disconnect is still recorded.
        telemetry.emit(ctx, "session_disconnected", { reason: token(info?.reason) }, generation);
        if (gen !== generation) return;
        opts.onDisconnect?.(info);
      },
      onUnhandledClientToolCall(call) {
        if (gen !== generation) return;
        opts.onUnhandledClientToolCall?.(call);
      },
      onRawEvent(e) {
        // Pass-through only: the raw stream never touches session state. Still generation
        // guarded, so a superseded session's trailing events can't reach the host handler.
        if (gen !== generation) return;
        opts.onRawEvent?.(e);
      },
    };
    return { events, surfacedError: () => surfaced };
  }

  function resolveNegotiate(tel: AuthEndpointTelemetryContext): SdpNegotiator | undefined {
    if (opts.negotiate) {
      const custom = opts.negotiate;
      // A custom host negotiator owns its own transport; wrap it only to inject the attempt
      // IDs and tag a rejection as the `negotiate` stage before rethrowing. The
      // per-attempt latch dedupes the outer `provider` re-emit from the connect catch. The
      // built-in authEndpoint negotiator tags `broker` itself.
      return async (offer: string) => {
        try {
          return await custom(offer, { correlationId: tel.ctx.correlationId, widgetInstanceId });
        } catch (e) {
          tel.emitter.emitFailureOnce(
            tel.ctx,
            "negotiate",
            { code: undefined, recoverable: false },
            tel.generation,
          );
          throw e;
        }
      };
    }
    if (opts.authEndpoint) {
      return makeAuthEndpointNegotiator(
        opts.authEndpoint,
        opts.provider,
        opts.config,
        opts.widgetId,
        opts.fetchCredentials ?? "include",
        tel,
      );
    }
    return undefined;
  }

  function resolveSessionEventSender(tel: AuthEndpointTelemetryContext) {
    if (!opts.authEndpoint) return undefined;
    return makeAuthEndpointSessionEventSender(
      opts.authEndpoint,
      opts.fetchCredentials ?? "include",
      tel,
    );
  }

  function resolveSessionRequester(
    tel: AuthEndpointTelemetryContext,
  ): SessionRequester | undefined {
    if (!opts.authEndpoint) return undefined;
    return makeAuthEndpointSessionRequester(
      opts.authEndpoint,
      opts.provider,
      opts.config,
      opts.widgetId,
      opts.fetchCredentials ?? "include",
      tel,
    );
  }

  function resolveSessionEnder(tel: AuthEndpointTelemetryContext) {
    // Mirror resolveNegotiate's precedence: a custom `negotiate` owns session creation,
    // so the authEndpoint may not hold the session and must not receive its `/end`. Only
    // wire the authEndpoint ender when the authEndpoint negotiator is the creation path.
    if (!opts.authEndpoint || opts.negotiate) return undefined;
    return makeAuthEndpointSessionEnder(opts.authEndpoint, opts.fetchCredentials ?? "include", tel);
  }

  async function resolveGrant(ctx: AttemptContext): Promise<SessionGrant> {
    if (opts.getSession) {
      // A custom getSession host receives the attempt's correlation IDs so its own transport
      // can propagate them to the broker. The synthetic/authEndpoint path carries them
      // through the negotiate ctx instead.
      const req: SessionRequest = {
        provider: opts.provider,
        config: opts.config,
        correlationId: ctx.correlationId,
        widgetInstanceId,
      };
      return opts.getSession(req);
    }
    // No pre-resolved grant: the provider owns how it connects (from its own
    // provider-config), so the core has nothing to contribute here.
    return {};
  }

  async function start(): Promise<void> {
    if (state.status === "connecting" || state.status === "connected") return;
    const gen = ++generation;
    // Retire any lingering prior attempt (a failed or passively-disconnected one whose context
    // was never cleared) as "superseded" before this attempt takes ownership.
    telemetry.retireAttempt("superseded", gen);
    const ctx = telemetry.createAttempt(gen);
    const { events, surfacedError } = attemptEvents(gen, ctx);
    telemetry.emit(ctx, "attempt_started", { provider: token(opts.provider) }, gen);
    // Set connecting synchronously (before the first await) so a second same-tick
    // start() sees it and bails; clears any prior error.
    setState({ status: "connecting", error: null });
    try {
      // Tag a getSession rejection as the `grant` stage (a provider.connect() throw is tagged
      // `provider` by the outer catch). Rethrow into the outer catch; the latch dedupes.
      let grant: SessionGrant;
      try {
        grant = await resolveGrant(ctx);
      } catch (e) {
        telemetry.emitFailureOnce(
          ctx,
          "grant",
          { code: undefined, recoverable: false },
          generation,
        );
        throw e;
      }
      if (gen !== generation) return; // stopped/superseded before we connected
      telemetry.emit(
        ctx,
        "grant_resolved",
        { source: opts.getSession ? "getSession" : "synthetic" },
        gen,
      );
      // A start over authEndpoint defers config resolution to the broker: the synthetic
      // direct grant carries no resolvedConfig and opts.config is empty, so there is nothing
      // to parse client-side — the broker resolves it during negotiation. (A getSession flow
      // that returns resolvedConfig is parsed here.) Parsing {} would throw before the
      // negotiator ever runs.
      const rawConfig = grant.resolvedConfig ?? opts.config;
      const config =
        Object.keys(rawConfig).length > 0 || !opts.authEndpoint
          ? provider!.parseConfig(rawConfig)
          : rawConfig;
      const clientTools = Object.fromEntries(
        registry.list().map((name) => [name, registry.get(name)!]),
      );
      snapshotBoundGen = gen;
      // The broker HTTP hops (negotiate/update/end) emit their own telemetry against
      // this attempt. Harmless to build even when no authEndpoint/negotiate is wired.
      const brokerTel: AuthEndpointTelemetryContext = {
        ctx,
        emitter: telemetry,
        generation: gen,
        widgetInstanceId,
      };
      await provider!.connect(grant, config, events, {
        requestSession: resolveSessionRequester(brokerTel),
        negotiate: resolveNegotiate(brokerTel),
        clientTools,
        clientToolRegistry: registry,
        sendSessionEvent: resolveSessionEventSender(brokerTel),
        endSession: resolveSessionEnder(brokerTel),
      });
      if (gen !== generation) {
        await provider!.disconnect().catch(() => {}); // orphaned session — tear down
      }
    } catch (e) {
      clearSnapshotFor(gen);
      if (gen === generation && !surfacedError()) {
        events.onError?.({
          code: "connect_failed",
          message: e instanceof Error ? e.message : String(e),
          recoverable: false,
        });
      }
    }
  }

  async function stop(): Promise<void> {
    // No-op only when there is nothing to stop. "error" is deliberately NOT guarded
    // here: stopping from an errored session acts as a dismiss — disconnect (idempotent
    // for an already-dead session) and reset to a clean idle, clearing the error so the
    // user can retry. This matches the pre-extraction mountVoiceAgent behavior.
    if (state.status === "idle" || state.status === "disconnected") return;
    // A voluntary stop from an ACTIVE session is a session-end: emit onDisconnect with
    // reason "stopped". Guarding to connecting/connected means a stop that
    // merely dismisses an already-errored session does NOT double-fire onDisconnect (the
    // provider's failed path already emitted one). A passive drop keeps the provider's
    // reason ("disconnected"/"closed"/"failed"); "stopped" marks the voluntary case.
    const wasActive = state.status === "connecting" || state.status === "connected";
    // Retire the attempt as "stopped" BEFORE generation++. retireAttempt returns the retired
    // context so the core-driven finally can keep emitting on it: stop() does
    // not route through attemptEvents and retirement cleared the emitter's current attempt.
    const retiredCtx = telemetry.retireAttempt("stopped", generation);
    generation++; // cancels any in-flight start()
    snapshotBoundGen = undefined;
    try {
      await provider!.disconnect();
    } finally {
      // Callback order on an active stop is pinned by a test: setState() below emits
      // onMuted(false) first (muted-reset is part of the snapshot), THEN onStatusChange
      // ("idle") and onDisconnect({reason:"stopped"}). Keep this order if refactoring.
      setState({ status: "idle", mode: null, muted: false, error: null });
      // Mirror provider-driven transitions (which call opts.onStatusChange in the events
      // handler): this core-driven idle must reach imperative callback consumers too.
      opts.onStatusChange?.("idle");
      if (wasActive) opts.onDisconnect?.({ reason: "stopped" });
      // Telemetry mirrors the same core-driven transitions on the retired context, emitted
      // AFTER the host callbacks so their pinned order is untouched. retiredAs="stopped"
      // makes both events carry attemptStatus "stopped".
      if (retiredCtx) {
        telemetry.emit(retiredCtx, "status_changed", { status: "idle" }, generation);
        if (wasActive) {
          telemetry.emit(retiredCtx, "session_disconnected", { reason: "stopped" }, generation);
        }
      }
    }
  }

  return {
    start,
    stop,
    setMuted(muted: boolean) {
      provider!.setMuted(muted);
      setState({ muted });
    },
    registerClientTool(name, handler) {
      warnIfBound("registration", name);
      const unregister = registry.register(name, handler);
      return () => {
        warnIfBound("unregistration", name);
        unregister();
      };
    },
    async sendText(text: string) {
      const normalized = text.trim();
      if (!normalized) throw new Error("createVoiceAgent: text must not be blank.");
      if (state.status !== "connected") {
        throw new Error("createVoiceAgent: text can only be sent while connected.");
      }
      if (!provider!.capabilities.supportsText || !provider!.sendText) {
        throw new Error(`createVoiceAgent: provider "${provider!.name}" does not support text.`);
      }
      const gen = generation;
      const previousMode = state.mode;
      setState({ mode: "thinking" });
      opts.onModeChange?.("thinking");
      try {
        await provider!.sendText(normalized);
        // A disconnect/reconnect during the (possibly HTTP) round trip supersedes this
        // turn; its terminal handler already reset mode, so don't emit a stale transcript.
        if (gen !== generation || state.status !== "connected") return;
        // Typed input may not be echoed by the runtime as an audio-transcription event,
        // so surface the local user turn through the same transcript channel immediately.
        opts.onTranscript?.({ role: "user", text: normalized, final: true });
      } catch (error) {
        // Same guard: if the session already terminated, its handler set the correct
        // terminal mode (null) — restoring previousMode here would carry a stale turn
        // mode into an error/disconnected state.
        if (gen !== generation || state.status !== "connected") throw error;
        setState({ mode: previousMode });
        if (previousMode) opts.onModeChange?.(previousMode);
        throw error;
      }
    },
    getState() {
      return state;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getOutputLevel() {
      return provider!.getOutputLevel();
    },
    destroy() {
      // Retire the attempt as "destroyed" BEFORE generation++. destroy() does not
      // cancel in-flight work, so late callbacks still read "destroyed" via their context.
      telemetry.retireAttempt("destroyed", generation);
      generation++;
      snapshotBoundGen = undefined;
      listeners.clear();
      void provider!.disconnect().catch(() => {});
    },
  };
}
